import { Injectable } from "@angular/core";
import { BaseModel } from "../models/base.model";

export interface School extends BaseModel{
  title?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private readonly managementGoogleCookieName = 'ativedu_management_token';

  /**
   * Get JWT token from sessionStorage or localStorage.
   */
  getToken(): string {
    return sessionStorage.getItem('jwtToken') || window.localStorage['jwtToken'];
  }

  /**
   * Get the Google login cookie token (set by GET /management/login/google/callback).
   */
  getCookieToken(): string | null {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${this.managementGoogleCookieName}=`));
    return match ? decodeURIComponent(match.split('=')[1]) : null;
  }

  /**
   * Clear the Google login cookie token.
   *
   * A exclusão de cookie só funciona se name/path/domain baterem com os do Set-Cookie
   * original. Como o backend pode ter setado o cookie com um atributo Domain (ex.:
   * `.ativedu.com.br`), apagar só com `path=/` falha em silêncio e o cookie sobrevive
   * ao logout — o Login o promoveria de volta ao localStorage e relogaria o usuário.
   * Por isso tentamos todas as variantes: sem domain, hostname exato e domínios-pai.
   */
  clearCookieToken(): void {
    const expire = 'expires=Thu, 01 Jan 1970 00:00:00 UTC; max-age=0; path=/';
    const name = this.managementGoogleCookieName;
    document.cookie = `${name}=; ${expire};`;
    const hostname = window.location.hostname;
    document.cookie = `${name}=; ${expire}; domain=${hostname};`;
    const parts = hostname.split('.');
    for (let i = 1; i < parts.length - 1; i++) {
      document.cookie = `${name}=; ${expire}; domain=.${parts.slice(i).join('.')};`;
    }
  }

  /**
   * Save JWT token to localStorage.
   */
  saveToken(token: string | null) {
    this.destroyToken();
    window.localStorage['jwtToken'] = token;
  }

  /**
   * Remove JWT token and school info from storage.
   */
  destroyToken() {
    window.localStorage.removeItem('jwtToken');
    sessionStorage.removeItem('jwtToken');
    this.clearCookieToken();
  }

  private base64UrlDecode(str: string): string {
    // Adiciona padding se necessário
    str = str.replace(/-/g, '+').replace(/_/g, '/');
    switch (str.length % 4) {
      case 0:
        break;
      case 2:
        str += '==';
        break;
      case 3:
        str += '=';
        break;
      default:
        throw new Error('Invalid base64 string');
    }
    return decodeURIComponent(escape(window.atob(str)));
  }

  /**
   * Decode JWT token payload.
   */
  decodeToken(token: string): any {
    try {
      const payload = token.split('.')[1];
      const decodedPayload = this.base64UrlDecode(payload);
      return JSON.parse(decodedPayload);
    } catch (Error) {
      console.error('Invalid token');
      return null;
    }
  }

  /**
   * Get decoded JWT token payload.
   */
  getDecodedToken(token: string | null): any {
    if (token == null) {
      return null;
    }
    const decodedToken = this.decodeToken(token);
    return decodedToken;
  }
}
